import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
import { TeamsDeeplink } from './intervisie.js';
/**
 * A SUPPOHRT-intern agenda event (trainingen, webinars, borrels).
 *
 * Firestore path: tenants/{tenantId}/agenda_events/{id}
 *
 * `date` is a plain NL local date string (YYYY-MM-DD), not a full timestamp:
 * events are all-day/wall-clock in the Netherlands, so storing a date string
 * avoids timezone drift and sorts chronologically as-is (no composite index
 * needed — orderBy(date) uses the automatic single-field index). `time` is a
 * free display string ("09:30 - 16:00") because events vary (single time,
 * range, "hele dag").
 */
const DateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet YYYY-MM-DD zijn');
/**
 * De beschrijving komt uit dezelfde rich-text-editor als een nieuwsbericht en
 * mag dus net zo lang zijn (nieuws staat op 50.000). Op 5.000 liep een normale
 * beschrijving stuk: opgemaakte HTML telt mee, en één geplakte afbeelding komt
 * als data-URI binnen en is in zijn eentje al groter dan de hele limiet. De
 * afwijzing kwam terug als "Request payload failed validation" en de hele
 * bewerking was daarmee weg.
 */
export const AGENDA_DESCRIPTION_MAX = 50_000;
/**
 * Evenementtype — gecureerde enum (besluit 30 juli 2026). Een waarde toevoegen
 * is bewust een codewijziging: het type stuurt zichtbaarheid (`intervisie` is
 * groepsgebonden) en de Webinars-projectie (`nascholing`).
 */
export const AgendaEventType = z.enum(['nascholing', 'teambuilding', 'intervisie', 'overig']);
export const AgendaOrganisator = z.object({
    userId: DocId,
    displayName: z.string().min(1).max(200),
});
/**
 * Spreker of trainer. Met `userId` is het een collega (link naar het
 * Smoelenboek); zonder is het een externe trainer en staat alleen de naam.
 */
export const AgendaSpreker = z.object({
    naam: z.string().min(1).max(200),
    userId: DocId.optional(),
});
export const AgendaEventSchema = z.object({
    id: DocId,
    tenantId: TenantId,
    title: z.string().min(1).max(200),
    date: DateString,
    time: z.string().max(50),
    /** Naam van de locatie ("De VerbroederIJ"). */
    location: z.string().max(200).optional(),
    /** Volledig adres, los van de naam, zodat kaart + agenda-export erop kunnen navigeren. */
    address: z.string().max(300).optional(),
    description: z.string().max(AGENDA_DESCRIPTION_MAX).optional(),
    /**
     * Leest soepel: docs van vóór 31 juli 2026 hebben dit veld niet en een
     * onbekende waarde mag een item nooit uit de lijst laten vallen (die stille
     * uitval kostte de Sociale Kaart 353 praktijken). Alles wat niet in de enum
     * zit leest als `overig`.
     */
    evenementtype: AgendaEventType.catch('overig').default('overig'),
    /**
     * Handmatig geplakte Teams-join-link (ADR 0014: link-doorgifte, geen Graph).
     * Bij type `intervisie` mag het item een eigen link hebben (één vergadering
     * per datum); ontbreekt die, dan vult het lezen de link van de groep in.
     */
    teamsUrl: TeamsDeeplink.optional(),
    /**
     * Leesprojectie, geen opgeslagen veld: `teamsUrl` is de terugval-link van de
     * groep, niet een eigen link van dit item. Het bewerkformulier laat het veld
     * dan leeg, anders bevriest opslaan de groepslink als eigen link.
     */
    teamsUrlVanGroep: z.boolean().optional(),
    /** Alleen bij type `intervisie`: de groep die de sessie mag zien. */
    intervisieGroupId: DocId.optional(),
    /**
     * Leesprojectie, geen opgeslagen veld: de naam van de groep wordt bij het
     * lezen live meegegeven, net als de Teams-link. Een kopie op het item zou
     * verouderen zodra de groep hernoemd wordt, en dan staat er in de agenda van
     * een lid een groepsnaam die nergens meer bestaat.
     */
    intervisieGroupNaam: z.string().max(120).optional(),
    /** Toont de "Ik ben erbij!"-knop. Uit voor mededelingen zonder deelnemers. */
    registrationsOpen: z.boolean().optional(),
    /**
     * Leesprojectie, geen opgeslagen veld (#334): het item heeft een Teams-link,
     * maar die gaat alleen naar aangemelde deelnemers, de organisator en beheer.
     * Voor de rest laat de backend `teamsUrl` weg en zet dit aan, zodat de UI
     * "Meld je aan om de link te zien" kan tonen.
     */
    teamsUrlNaAanmelding: z.boolean().optional(),
    /**
     * Wie het evenement geeft of regelt (issue #220, besluit Jos 17 sep 2026).
     * Een organisator die geen kantoorpersoneel is (bv. een POH-GGZ die een
     * trainingsdag geeft) ziet de aanmeldingen van dít item, niet van andere.
     * Naam is een snapshot zodat de lijst zonder extra reads te tonen is.
     * Leest soepel: een kapotte waarde laat het item nooit uit de lijst vallen.
     */
    organisatoren: z.array(AgendaOrganisator).max(10).optional().catch(undefined),
    sprekers: z.array(AgendaSpreker).max(10).optional().catch(undefined),
    authorId: DocId,
    authorName: z.string().max(100),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp.optional(),
});
/**
 * Eén aanmelding voor een evenement.
 *
 * Firestore path: tenants/{tenantId}/agenda_events/{eventId}/registrations/{userId}
 *
 * De userId ís de document-id: aanmelden is daarmee idempotent en dubbele
 * aanmeldingen bestaan niet. Naam/e-mail/lidnummers zijn een kopie van het
 * profiel op het moment van aanmelden, zodat de deelnemerslijst één read is
 * en achteraf laat zien wat er bij aanmelding bekend was.
 */
export const AgendaRegistrationSchema = z.object({
    userId: DocId,
    displayName: z.string().min(1).max(200),
    email: z.string().email(),
    note: z.string().max(1000).optional(),
    lidnummerLvpoh: z.string().max(50).optional(),
    lidnummerSkj: z.string().max(50).optional(),
    createdAt: IsoTimestamp,
});
export const CreateAgendaRegistrationInput = z.object({
    note: z
        .string()
        .max(1000)
        .trim()
        .optional()
        .or(z.literal(''))
        .transform((v) => v || undefined),
});
const optionalText = (max) => z
    .string()
    .max(max)
    .trim()
    .optional()
    .or(z.literal(''))
    .transform((v) => v || undefined);
/**
 * Een leeggemaakt formulierveld komt als '' binnen; dat is "geen link", geen
 * ongeldige URL. Alles wat wél ingevuld is gaat door de Teams-validator.
 */
const teamsUrlInput = TeamsDeeplink.optional()
    .or(z.literal(''))
    .transform((v) => v || undefined);
/**
 * Bij een update is het verschil tussen "niet meegestuurd" en "leeggemaakt"
 * betekenisvol: afwezig laat de opgeslagen waarde staan, een leeg veld wist
 * hem (`null` → `FieldValue.delete()` in de service). Zonder dit onderscheid
 * is een eenmaal geplakte Teams-link niet meer weg te halen.
 */
const clearable = (schema) => schema
    .optional()
    .or(z.literal(''))
    .transform((v) => v === undefined ? undefined : v === '' ? null : v);
/**
 * Type en groep horen bij elkaar: een intervisiesessie zonder groep zou voor
 * niemand zichtbaar zijn, en een groep op een gewoon item zou suggereren dat
 * het item afgeschermd is terwijl heel SUPPOHRT-intern het ziet.
 *
 * Een eigen `teamsUrl` mag sinds 2 nov 2026 ook bij intervisie: de groepen
 * krijgen één Teams-vergadering per datum, niet één terugkerende. De eigen
 * link gaat voor, de groepslink is terugval (ADR 0014, bijgewerkt).
 *
 * Bij een update zonder `evenementtype` in de body geldt de "geen groep"-tak:
 * wie de groepskoppeling wijzigt, stuurt het type mee.
 */
function typeEnGroepRegel(v, ctx) {
    if (v.evenementtype === 'intervisie') {
        if (!v.intervisieGroupId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['intervisieGroupId'],
                message: 'Kies een intervisiegroep bij het type intervisie.',
            });
        }
        return;
    }
    // `null` is "wis de koppeling" — dat mag juist bij een ander type.
    if (v.intervisieGroupId != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['intervisieGroupId'],
            message: 'Een intervisiegroep hoort alleen bij het type intervisie.',
        });
    }
}
export const CreateAgendaEventInput = z
    .object({
    title: z.string().min(1).max(200).trim(),
    date: DateString,
    time: z.string().min(1).max(50).trim(),
    location: optionalText(200),
    address: optionalText(300),
    description: optionalText(AGENDA_DESCRIPTION_MAX),
    // Default `overig` zodat een client die het type (nog) niet stuurt geen
    // 400 krijgt; een onbekende waarde faalt hier wél, anders dan bij lezen.
    evenementtype: AgendaEventType.default('overig'),
    teamsUrl: teamsUrlInput,
    intervisieGroupId: DocId.optional(),
    registrationsOpen: z.boolean().optional(),
    organisatoren: z.array(AgendaOrganisator).max(10).optional(),
    sprekers: z.array(AgendaSpreker).max(10).optional(),
})
    .superRefine(typeEnGroepRegel);
export const UpdateAgendaEventInput = z
    .object({
    title: z.string().min(1).max(200).trim().optional(),
    date: DateString.optional(),
    time: z.string().min(1).max(50).trim().optional(),
    location: clearable(z.string().max(200).trim()),
    address: clearable(z.string().max(300).trim()),
    description: clearable(z.string().max(AGENDA_DESCRIPTION_MAX).trim()),
    evenementtype: AgendaEventType.optional(),
    teamsUrl: clearable(TeamsDeeplink),
    intervisieGroupId: clearable(DocId),
    registrationsOpen: z.boolean().optional(),
    /** Lege lijst = niemand meer; niet meegestuurd = ongewijzigd. */
    organisatoren: z.array(AgendaOrganisator).max(10).optional(),
    sprekers: z.array(AgendaSpreker).max(10).optional(),
})
    .superRefine(typeEnGroepRegel);
//# sourceMappingURL=agenda.js.map